"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppBaseFrameCompositionError = exports.APP_BASE_FRAME_SIDE_ID = exports.nestAppNavigation = void 0;
exports.readAppBaseFrameContent = readAppBaseFrameContent;
exports.composeAppBaseFrameDocument = composeAppBaseFrameDocument;
exports.rebaseRelativeUrl = rebaseRelativeUrl;
const app_base_contract_1 = require("./app-base-contract");
const app_base_design_baseline_1 = require("./app-base-design-baseline");
const app_base_nav_mark_1 = require("./app-base-nav-mark");
const app_base_navigation_contract_1 = require("./app-base-navigation-contract");
const app_base_portal_page_text_1 = require("./app-base-portal-page-text");
var app_base_contract_2 = require("./app-base-contract");
Object.defineProperty(exports, "nestAppNavigation", { enumerable: true, get: function () { return app_base_contract_2.nestAppNavigation; } });
/** The id of the side navigation, which its toggle in the top bar controls. */
exports.APP_BASE_FRAME_SIDE_ID = 'sl-frame-side';
/** One source reader for server delivery, StreamKit and the flow inspector. */
async function readAppBaseFrameContent(input) {
    const frame = input.frame;
    return {
        brand: {
            name: frame
                ? navigationLabel({ label: frame.brand.name, labelsI18n: frame.brand.namesI18n }, input.locale)
                : input.name,
            ...(frame?.brand.logo ? { logoPath: frame.brand.logo } : {}),
        },
        ...(frame?.stylesheet ? { stylesheetPath: frame.stylesheet } : {}),
        ...(frame?.foot
            ? { foot: { html: await input.readText(frame.foot), path: frame.foot } }
            : {}),
        panels: await Promise.all(input.topBar.flatMap(item => {
            const path = item.panel?.entry;
            return path
                ? [input.readText(path).then(html => ({ id: item.id, html, path }))]
                : [];
        })),
    };
}
class AppBaseFrameCompositionError extends Error {
}
exports.AppBaseFrameCompositionError = AppBaseFrameCompositionError;
const URL_ATTRIBUTES = ['href', 'src'];
function navigationLabel(item, locale) {
    const labels = item.labelsI18n;
    if (!locale ||
        !labels ||
        !Object.prototype.hasOwnProperty.call(labels, locale)) {
        return item.label;
    }
    return labels[locale] ?? item.label;
}
function composeAppBaseFrameDocument(input) {
    const page = input.parseDocument(input.pageHtml);
    const frame = input.parseDocument('<html><head></head><body></body></html>');
    const platformText = app_base_portal_page_text_1.APP_BASE_FRAME_NAVIGATION_TEXT[(0, app_base_portal_page_text_1.appBasePlatformPageLanguage)(input.locale ?? 'en')];
    const element = (tag, attribute, parent) => {
        const node = frame.createElement(tag);
        node.setAttribute(attribute, '');
        parent.appendChild(node);
        return node;
    };
    const prependAssets = (document) => {
        const keys = new Set([...frame.head.children, ...page.head.children]
            .filter(isFrameHeadAsset)
            .map(assetKey));
        for (const node of [...document.head.childNodes]) {
            if (!isFrameHeadAsset(node))
                continue;
            const key = assetKey(node);
            if (keys.has(key))
                continue;
            keys.add(key);
            frame.head.appendChild(node);
        }
    };
    const insertContent = (target, content) => {
        const document = input.parseDocument(content.html);
        for (const node of document.querySelectorAll('[href], [src]')) {
            for (const attribute of URL_ATTRIBUTES) {
                const value = node.getAttribute(attribute);
                if (value !== null)
                    node.setAttribute(attribute, rebaseRelativeUrl(value, content.path, input.pagePath));
            }
        }
        prependAssets(document);
        target.innerHTML = document.body.innerHTML;
    };
    const style = element('style', 'data-sl-frame-layout-style', frame.head);
    style.textContent = (0, app_base_design_baseline_1.renderAppBaseFrameLayoutStylesheet)();
    if (input.stylesheetPath) {
        const stylesheet = frame.createElement('link');
        stylesheet.setAttribute('rel', 'stylesheet');
        stylesheet.setAttribute('href', rebaseRelativeUrl(input.stylesheetPath, 'index.html', input.pagePath));
        if (![...page.head.children]
            .filter(isFrameHeadAsset)
            .some(node => assetKey(node) === assetKey(stylesheet)))
            frame.head.appendChild(stylesheet);
    }
    const header = element('header', 'data-sl-frame-top', frame.body);
    const mainNavigation = input.navigation.filter(item => (item.placement ?? 'primary') === 'primary');
    const hasSide = input.layout === 'side' && mainNavigation.length > 0;
    if (hasSide) {
        // The side column folds away from the top bar; the navigation runtime
        // keeps the choice (body[data-sl-frame-side-collapsed]).
        const toggle = element('button', 'data-sl-frame-side-toggle', header);
        toggle.setAttribute('type', 'button');
        toggle.setAttribute('aria-controls', exports.APP_BASE_FRAME_SIDE_ID);
        toggle.setAttribute('aria-expanded', 'true');
        toggle.setAttribute('aria-label', platformText.side);
        element('span', 'data-sl-frame-side-toggle-icon', toggle);
    }
    const brand = element('a', 'data-sl-frame-brand', header);
    brand.setAttribute('href', input.routeHref('/'));
    brand.setAttribute('data-sl-route', '/');
    if (input.brand.logoPath) {
        const logo = frame.createElement('img');
        logo.setAttribute('src', rebaseRelativeUrl(input.brand.logoPath, 'index.html', input.pagePath));
        logo.setAttribute('alt', '');
        brand.appendChild(logo);
    }
    const brandName = frame.createElement('span');
    brandName.textContent = input.brand.name;
    brand.appendChild(brandName);
    const secondaryNavigation = input.navigation.filter(item => item.placement === 'secondary');
    if (mainNavigation.length) {
        // The side navigation is its own grid column next to the outlet, so it
        // sits between the top bar and the page in the reading order as well.
        const nav = hasSide
            ? element('nav', 'data-sl-frame-side', frame.body)
            : element('nav', app_base_contract_1.APP_BASE_FRAME_NAV_ATTRIBUTE, header);
        if (hasSide)
            nav.setAttribute('id', exports.APP_BASE_FRAME_SIDE_ID);
        nav.setAttribute('aria-label', platformText.label);
    }
    const outlet = element('main', app_base_contract_1.APP_BASE_FRAME_OUTLET_ATTRIBUTE, frame.body);
    let foot;
    if (secondaryNavigation.length || input.foot) {
        foot = element('footer', 'data-sl-frame-foot', frame.body);
        if (secondaryNavigation.length) {
            const nav = element('nav', app_base_contract_1.APP_BASE_FRAME_NAV_ATTRIBUTE, foot);
            nav.setAttribute(app_base_contract_1.APP_BASE_FRAME_NAV_ATTRIBUTE, 'secondary');
            nav.setAttribute('aria-label', platformText.label);
        }
        if (input.foot)
            insertContent(element('div', 'data-sl-frame-foot-content', foot), input.foot);
    }
    const primary = input.navigation.filter(item => item.primary);
    const primaryIssue = (0, app_base_navigation_contract_1.appNavigationPrimaryIssue)(input.navigation);
    if (primaryIssue)
        throw new AppBaseFrameCompositionError(primaryIssue.message);
    const appendEntryContent = (link, item) => {
        if (item.icon) {
            const icon = frame.createElement('img');
            // Compiled icons are artifact-root paths, independent of the frame provider.
            icon.setAttribute('src', rebaseRelativeUrl(item.icon, 'index.html', input.pagePath));
            icon.setAttribute('alt', '');
            icon.setAttribute('data-sl-nav-icon', '');
            link.appendChild(icon);
        }
        const label = frame.createElement('span');
        label.setAttribute('data-sl-nav-label', '');
        label.textContent = navigationLabel(item, input.locale);
        link.appendChild(label);
        if (item.badge) {
            const badge = frame.createElement('span');
            badge.setAttribute('data-sl-nav-badge', '');
            badge.setAttribute('data-sl-source', item.badge.sourceKey);
            badge.setAttribute('data-sl-count', '');
            badge.setAttribute('aria-live', 'polite');
            badge.setAttribute('aria-atomic', 'true');
            // Its dataset is declared on the route or in frame.dataSourceKeys,
            // so the existing binding delivery enforces access and read budgets.
            if (item.badge.filter) {
                badge.setAttribute('data-sl-filter-field', item.badge.filter.field);
                badge.setAttribute('data-sl-filter-key', item.badge.filter.key);
            }
            link.appendChild(badge);
        }
    };
    const renderNavigation = (nav, items, nested) => {
        nav.innerHTML = '';
        if (items.length === 0) {
            nav.appendChild(frame.createElement('ul'));
            return;
        }
        const byId = new Map(items.map(item => [item.id, item]));
        const isSubEntry = (item) => {
            const parent = item.parentId ? byId.get(item.parentId) : undefined;
            return nested && parent !== undefined && !parent.parentId;
        };
        const renderList = (entries, level) => {
            const list = frame.createElement('ul');
            list.setAttribute(app_base_contract_1.APP_BASE_FRAME_NAV_LEVEL_ATTRIBUTE, String(level));
            for (const item of entries) {
                const subEntries = level === 1
                    ? items.filter(sub => isSubEntry(sub) && sub.parentId === item.id)
                    : [];
                const entry = frame.createElement('li');
                const link = frame.createElement(item.path !== undefined ? 'a' : 'button');
                if (item.primary)
                    link.setAttribute('data-sl-nav-primary', '');
                if (item.path !== undefined) {
                    link.setAttribute('href', input.routeHref(item.path));
                    link.setAttribute('data-sl-route', item.path);
                }
                else if (item.action) {
                    link.setAttribute('type', 'button');
                    link.setAttribute('data-sl-nav-id', item.id);
                    link.setAttribute('data-sl-nav-action', item.action.type);
                    link.setAttribute('data-sl-nav-target', item.action.target);
                    if (item.action.type === 'overlay')
                        link.setAttribute('aria-controls', item.action.target);
                }
                appendEntryContent(link, item);
                entry.appendChild(link);
                if (subEntries.length)
                    entry.appendChild(renderList(subEntries, 2));
                list.appendChild(entry);
            }
            return list;
        };
        nav.appendChild(renderList(items.filter(item => !isSubEntry(item)), 1));
    };
    for (const nav of frame.querySelectorAll('[' + app_base_contract_1.APP_BASE_FRAME_NAV_ATTRIBUTE + '], [data-sl-frame-side]')) {
        const placement = nav.getAttribute(app_base_contract_1.APP_BASE_FRAME_NAV_ATTRIBUTE) === 'secondary'
            ? 'secondary'
            : 'primary';
        renderNavigation(nav, input.navigation.filter(item => (item.placement ?? 'primary') === placement), true);
    }
    if (input.topBar.length) {
        const actions = element('ul', 'data-sl-frame-actions', header);
        actions.setAttribute('aria-label', platformText.actions);
        for (const item of input.topBar) {
            const li = frame.createElement('li');
            actions.appendChild(li);
            const control = frame.createElement(item.path ? 'a' : 'button');
            control.setAttribute('data-sl-topbar-id', item.id);
            if (item.path) {
                control.setAttribute('href', input.routeHref(item.path));
                control.setAttribute('data-sl-route', item.path);
            }
            else {
                const content = input.panels.find(panel => panel.id === item.id);
                if (!content)
                    throw new AppBaseFrameCompositionError('Missing top bar panel ' + item.id);
                let id = 'sl-frame-panel-' + item.id.replace(/[^A-Za-z0-9_-]/g, '-');
                while (frame.getElementById(id) || page.getElementById(id))
                    id += '-panel';
                const popup = element('div', 'data-sl-frame-panel', frame.body);
                popup.setAttribute('data-sl-frame-panel', item.id);
                popup.id = id;
                popup.setAttribute('popover', 'auto');
                popup.setAttribute('role', 'dialog');
                popup.setAttribute('aria-label', navigationLabel(item, input.locale));
                insertContent(popup, content);
                control.setAttribute('type', 'button');
                control.setAttribute('popovertarget', id);
                control.setAttribute('aria-controls', id);
                control.setAttribute('aria-haspopup', 'dialog');
            }
            appendEntryContent(control, item);
            li.appendChild(control);
        }
    }
    if (input.navigation.length) {
        page.documentElement.setAttribute('data-sl-frame-adaptive', '');
        // The bar stays outside the page outlet.
        const bar = element('nav', 'data-sl-frame-bar', frame.body);
        bar.setAttribute('aria-label', platformText.label);
        renderNavigation(bar, primary, false);
        const rest = input.navigation.filter(item => !item.primary);
        if (rest.length) {
            let moreId = 'sl-frame-more';
            while (frame.getElementById(moreId) || page.getElementById(moreId))
                moreId += '-nav';
            const label = platformText.more;
            const more = frame.createElement('li');
            const button = frame.createElement('button');
            button.setAttribute('type', 'button');
            button.setAttribute('popovertarget', moreId);
            button.setAttribute('popovertargetaction', 'toggle');
            button.setAttribute('aria-controls', moreId);
            button.setAttribute('aria-haspopup', 'dialog');
            const icon = frame.createElement('span');
            icon.setAttribute('data-sl-nav-icon', '');
            icon.setAttribute('data-sl-nav-more-icon', '');
            icon.setAttribute('aria-hidden', 'true');
            button.appendChild(icon);
            const text = frame.createElement('span');
            text.setAttribute('data-sl-nav-label', '');
            text.textContent = label;
            button.appendChild(text);
            more.appendChild(button);
            bar.firstElementChild?.appendChild(more);
            const popup = frame.createElement('div');
            popup.id = moreId;
            popup.setAttribute('popover', 'auto');
            popup.setAttribute('role', 'dialog');
            popup.setAttribute('aria-label', label);
            popup.setAttribute('data-sl-frame-more', '');
            renderNavigation(popup, rest, true);
            bar.appendChild(popup);
        }
    }
    else {
        page.documentElement.removeAttribute('data-sl-frame-adaptive');
    }
    // The entries of the current page are marked as the page marks them when
    // it changes to another one.
    (0, app_base_nav_mark_1.markAppBaseNavRoute)(frame, input.currentRoutePath);
    outlet.innerHTML = page.body.innerHTML;
    page.body.innerHTML = frame.body.innerHTML;
    page.body.setAttribute('data-sl-frame-layout', input.layout);
    const first = page.head.firstChild;
    for (const node of [...frame.head.childNodes])
        page.head.insertBefore(node, first);
    return '<!DOCTYPE html>' + page.documentElement.outerHTML;
}
function assetKey(element) {
    return (element.tagName.toLowerCase() +
        ':' +
        (element.getAttribute('href') ??
            element.getAttribute('src') ??
            element.outerHTML));
}
function isFrameHeadAsset(node) {
    if (node.nodeType !== 1)
        return false;
    const element = node;
    const tag = element.tagName.toLowerCase();
    if (tag === 'style' || tag === 'script')
        return true;
    if (tag !== 'link')
        return false;
    const rel = element.getAttribute('rel')?.toLowerCase() ?? '';
    return rel
        .split(/\s+/)
        .some(token => token === 'stylesheet' || token === 'preload');
}
/**
 * Rewrites a frame-relative URL so it resolves from the page directory.
 * Absolute URLs, protocol URLs, fragments and data URIs stay unchanged.
 */
function rebaseRelativeUrl(value, sourcePath, pagePath) {
    const trimmed = value.trim();
    if (trimmed === '' ||
        trimmed.startsWith('/') ||
        trimmed.startsWith('#') ||
        trimmed.startsWith('?') ||
        /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
        return value;
    }
    const target = resolvePath(directoryOf(sourcePath), trimmed);
    const fromSegments = directoryOf(pagePath);
    let shared = 0;
    while (shared < fromSegments.length &&
        shared < target.length - 1 &&
        fromSegments[shared] === target[shared]) {
        shared += 1;
    }
    const up = fromSegments.length - shared;
    return [...Array(up).fill('..'), ...target.slice(shared)].join('/');
}
function directoryOf(path) {
    return path.split('/').filter(Boolean).slice(0, -1);
}
function resolvePath(directory, relative) {
    const segments = [...directory];
    for (const segment of relative.split('/')) {
        if (segment === '' || segment === '.')
            continue;
        if (segment === '..')
            segments.pop();
        else
            segments.push(segment);
    }
    return segments;
}
